import { Modal, clsx } from "@mantine/core";
import {
    OfferPublished,
    PublishedOfferAuditTrail,
} from "graphql/__generated__/globalTypes";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import config from "config";
import { usePublishedOfferAuditTrail } from "hooks/usePublishedOffer";
import { parseUrl } from "utils/helpers";
import AuditTrailEntry from "./AuditTrailEntry";
import PublisherPill from "./PublisherPill";

interface ParticipationHistoryContainerProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    offerPublished: OfferPublished;
    offerId: string;
}

const historyTabKeys = [
    { key: "history.offer_history", href: "OFFER_HISTORY" },
    { key: "history.participation_history", href: "PARTICIPATION_HISTORY" },
];

const ParticipationHistoryContainer: FC<ParticipationHistoryContainerProps> = ({
    open,
    setOpen,
    offerPublished,
    offerId,
}) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState("OFFER_HISTORY");
    const [selectedActor, setSelectedActor] = useState<string>("");
    const [dateFrom, setDateFrom] = useState<string>("");
    const [dateTo, setDateTo] = useState<string>("");

    const {
        auditTrail,
        loading: auditLoading,
        error: auditError,
        refresh,
    } = usePublishedOfferAuditTrail({
        published_offer_id: offerPublished.offer_published_id,
    });

    const [reportContent, setReportContent] = useState<string>("");
    const [reportLoading, setReportLoading] = useState(false);
    const [reportError, setReportError] = useState<string | null>(null);

    const uniqueActors = useMemo(() => {
        if (!auditTrail) return [];
        const actors = auditTrail
            .filter(
                (entry): entry is PublishedOfferAuditTrail => entry !== null,
            )
            .map((entry) => entry.actor)
            .filter((actor): actor is string => Boolean(actor));
        return Array.from(new Set(actors)).sort();
    }, [auditTrail]);

    const filteredAuditTrail = useMemo(() => {
        if (!auditTrail) return [];

        return auditTrail
            .filter(
                (entry): entry is PublishedOfferAuditTrail => entry !== null,
            )
            .filter((entry) => {
                if (selectedActor && entry.actor !== selectedActor)
                    return false;

                if (dateFrom || dateTo) {
                    const entryDate = entry.created_at
                        ? new Date(entry.created_at)
                        : null;
                    if (!entryDate) return false;

                    if (dateFrom && entryDate < new Date(dateFrom))
                        return false;
                    if (dateTo && entryDate > new Date(dateTo + "T23:59:59"))
                        return false;
                }

                return true;
            });
    }, [auditTrail, selectedActor, dateFrom, dateTo]);

    const clearFilters = () => {
        setSelectedActor("");
        setDateFrom("");
        setDateTo("");
    };

    useEffect(() => {
        if (open && offerId && activeTab === "OFFER_HISTORY") {
            fetchReportData();
        }
    }, [open, offerId, activeTab]);

    const fetchReportData = async () => {
        setReportLoading(true);
        setReportError(null);
        try {
            const response = await fetch(
                parseUrl(`${config.asset.backendUri}/historic/${offerId}`, {
                    forceHttps: true,
                    defaultProtocol: "https",
                }),
            );

            if (!response.ok) {
                throw new Error(
                    `Failed to fetch report: ${response.status} ${response.statusText}`,
                );
            }

            const htmlContent = await response.text();
            setReportContent(htmlContent);
        } catch (err) {
            console.error("Error fetching report data:", err);
            setReportError(
                err instanceof Error ? err.message : t("common.unknown_error"),
            );
        } finally {
            setReportLoading(false);
        }
    };

    const handleTabChange = (tabKey: string) => {
        setActiveTab(tabKey);
        if (tabKey === "PARTICIPATION_HISTORY") {
            refresh();
        }
    };

    const formatDate = (dateString?: string | null) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const isParticipationAccepted =
        offerPublished.reinsurer_participation?.participation_status ===
        "ACCEPTED";

    const isPlaceItOffer =
        offerPublished.published_by?.toLowerCase() === "placeit";

    const renderOfferHistory = () => {
        if (reportLoading) {
            return (
                <div className="flex justify-center py-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700"></div>
                </div>
            );
        }

        if (reportError) {
            return (
                <div className="bg-red-50 p-4 rounded-md">
                    <p className="text-red-700">
                        {t("reports.error_loading")}: {reportError}
                    </p>
                </div>
            );
        }

        return (
            <div
                className="report-container max-h-96 overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: reportContent }}
            />
        );
    };

    const renderAuditTrail = () => {
        if (auditLoading) {
            return (
                <div className="flex justify-center py-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700"></div>
                </div>
            );
        }

        if (auditError) {
            return (
                <div className="text-center py-10">
                    <p className="text-red-600">
                        {t("history.error_loading_audit")}: {auditError.message}
                    </p>
                </div>
            );
        }

        if (!auditTrail || auditTrail.length === 0) {
            return (
                <div className="text-center py-10">
                    <p className="text-gray-500">{t("history.no_entries")}</p>
                </div>
            );
        }

        return (
            <div>
                <div className="bg-gray-50 p-4 rounded-md mb-4 border border-gray-200">
                    <div className="flex flex-wrap gap-4 items-end">
                        <div className="flex-1 min-w-48">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("history.filter_by_actor")}
                            </label>
                            <select
                                value={selectedActor}
                                onChange={(e) =>
                                    setSelectedActor(e.target.value)
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500">
                                <option value="">{t("history.all_actors")}</option>
                                {uniqueActors.map((actor) => (
                                    <option key={actor} value={actor}>
                                        {actor}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex-1 min-w-32">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("history.from_date")}
                            </label>
                            <input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            />
                        </div>

                        <div className="flex-1 min-w-32">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("history.to_date")}
                            </label>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            />
                        </div>

                        <button
                            onClick={clearFilters}
                            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors">
                            {t("common.buttons.clear_filters")}
                        </button>
                    </div>

                    {(selectedActor || dateFrom || dateTo) && (
                        <div className="mt-3 text-sm text-gray-600">
                            {t("history.showing_entries", {
                                count: filteredAuditTrail.length,
                                total: auditTrail.filter((entry) => entry !== null).length,
                            })}
                        </div>
                    )}
                </div>

                <div className="space-y-4 max-h-96 overflow-y-auto">
                    {filteredAuditTrail.length === 0 ? (
                        <div className="text-center py-10">
                            <p className="text-gray-500">
                                {t("history.no_entries_filter")}
                            </p>
                        </div>
                    ) : (
                        filteredAuditTrail.map((entry, index) => (
                            <AuditTrailEntry
                                key={index}
                                entry={entry}
                                index={index}
                            />
                        ))
                    )}
                </div>
            </div>
        );
    };

    return (
        <Modal
            opened={open}
            onClose={() => setOpen(false)}
            title={t("modal_titles.history")}
            size="xl">
            <div className="bg-gray-50 p-4 rounded-md mb-4 border border-gray-200">
                <h4 className="font-medium text-gray-900 mb-3">
                    {t("offer_thread.offer_details")}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                    <p className="text-sm">
                        <span className="text-gray-500">{t("offer_thread.business_name")}:</span>{" "}
                        <span className="font-medium text-gray-800">
                            {isPlaceItOffer
                                ? offerPublished.offer.class_of_business
                                      ?.business_name || t("common.na")
                                : offerPublished.offer.classofbusiness
                                      ?.business_name || t("common.na")}
                        </span>
                    </p>
                    <p className="text-sm">
                        <span className="text-gray-500">{t("offer_thread.insured")}:</span>{" "}
                        <span className="font-medium text-gray-800">
                            {offerPublished.offer?.offer_detail?.insured_by ||
                                t("common.na")}
                        </span>
                    </p>
                    <p className="text-sm">
                        <span className="text-gray-500">{t("offer_thread.fac_offer")}:</span>{" "}
                        <span className="font-medium text-gray-800">
                            {offerPublished.offer?.facultative_offer || t("common.na")}%
                        </span>
                    </p>
                    <p className="text-sm">
                        <span className="text-gray-500">{t("offer_thread.policy_number")}:</span>{" "}
                        <span className="font-medium text-gray-800">
                            {offerPublished.offer?.offer_detail
                                ?.policy_number || t("common.na")}
                        </span>
                    </p>
                    <p className="text-sm">
                        <span className="text-gray-500">{t("offer_thread.period")}:</span>{" "}
                        <span className="font-medium text-gray-800">
                            {formatDate(
                                offerPublished.offer?.offer_detail
                                    ?.period_of_insurance_from,
                            )}
                        </span>{" "}
                        {t("offer_thread.period_to")}{" "}
                        <span className="font-medium text-gray-800">
                            {formatDate(
                                offerPublished.offer?.offer_detail
                                    ?.period_of_insurance_to,
                            )}
                        </span>
                    </p>
                    <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-500">{t("offer_thread.published_by")}:</span>
                        <PublisherPill
                            publishedBy={offerPublished.published_by}
                        />
                    </div>
                    {offerPublished.reinsurer_participation && (
                        <p className="text-sm md:col-span-2">
                            <span className="text-gray-500">
                                {t("offer_thread.your_interest", {
                                    status: isParticipationAccepted
                                        ? t("offer_thread.accepted_interest")
                                        : t("offer_thread.proposed_interest"),
                                })}:
                            </span>{" "}
                            <span
                                className={clsx(
                                    "font-medium",
                                    isParticipationAccepted
                                        ? "text-green-600"
                                        : "text-red-600",
                                )}>
                                {offerPublished?.reinsurer_participation
                                    ?.offer_participant_percentage || t("common.na")}
                                %
                            </span>
                        </p>
                    )}
                </div>
            </div>

            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                    {historyTabKeys.map((tab) => (
                        <button
                            key={tab.href}
                            onClick={() => handleTabChange(tab.href)}
                            className={`${
                                activeTab === tab.href
                                    ? "border-green-500 text-green-600"
                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                            aria-current={
                                activeTab === tab.href ? "page" : undefined
                            }>
                            {t(tab.key)}
                        </button>
                    ))}
                </nav>
            </div>

            <div className="mt-2">
                <p className="text-sm text-gray-500">
                    {activeTab === "OFFER_HISTORY"
                        ? t("history.offer_history_description")
                        : t("history.participation_history_description")}
                </p>
            </div>

            <div className="mt-5 mb-5">
                {activeTab === "OFFER_HISTORY"
                    ? renderOfferHistory()
                    : renderAuditTrail()}
            </div>
        </Modal>
    );
};

export default ParticipationHistoryContainer;
